// Validation utilities for forms and data

export function validateEmail(email: string): boolean {
  // RFC 5322 compliant email regex
  const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/
  return emailRegex.test(email)
}

export function validateNIF(nif: string): boolean {
  // Portuguese NIF (Número de Identificação Fiscal) validation
  if (!nif || nif.length !== 9) return false
  
  const digits = nif.split('').map(Number)
  const checkDigit = digits[8]
  
  let sum = 0
  for (let i = 0; i < 8; i++) {
    sum += digits[i] * (9 - i)
  }
  
  const remainder = sum % 11
  const expectedCheckDigit = remainder < 2 ? 0 : 11 - remainder
  
  return checkDigit === expectedCheckDigit
}

export function validatePassword(password: string): { isValid: boolean; errors: string[] } {
  const errors: string[] = []
  
  if (password.length < 8) {
    errors.push('A palavra-passe deve ter pelo menos 8 caracteres')
  }
  
  if (!/[A-Z]/.test(password)) {
    errors.push('A palavra-passe deve conter pelo menos uma letra maiúscula')
  }
  
  if (!/[a-z]/.test(password)) {
    errors.push('A palavra-passe deve conter pelo menos uma letra minúscula')
  }
  
  if (!/\d/.test(password)) {
    errors.push('A palavra-passe deve conter pelo menos um número')
  }
  
  return {
    isValid: errors.length === 0,
    errors
  }
}

export function validateRequired(value: string, fieldName: string): string | null {
  if (!value || value.trim().length === 0) {
    return `${fieldName} é obrigatório`
  }
  return null
}

export function validateRange(
  value: number,
  min: number,
  max: number,
  fieldName: string
): string | null {
  if (value < min || value > max) {
    return `${fieldName} deve estar entre ${min} e ${max}`
  }
  return null
}

export function validateYear(year: number): string | null {
  const currentYear = new Date().getFullYear()
  if (year < 2020 || year > currentYear + 1) {
    return `O ano deve estar entre 2020 e ${currentYear + 1}`
  }
  return null
}

export function validateMonth(month: number): string | null {
  if (month < 1 || month > 12) {
    return 'O mês deve estar entre 1 e 12'
  }
  return null
}

export function formatValidationErrors(errors: (string | null)[]): string[] {
  return errors.filter((error): error is string => error !== null)
}

export interface FormValidationResult {
  isValid: boolean
  errors: Record<string, string>
}

export function createValidator<T extends Record<string, any>>(
  validationRules: {
    [K in keyof T]?: (value: T[K]) => string | null
  }
) {
  return (data: T): FormValidationResult => {
    const errors: Record<string, string> = {}
    
    Object.entries(validationRules).forEach(([field, validate]) => {
      const error = validate!(data[field])
      if (error) {
        errors[field] = error
      }
    })
    
    return {
      isValid: Object.keys(errors).length === 0,
      errors
    }
  }
}
